import type { AccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import type { DeepPartial } from '@frameio/utils';
import TEXT from './generated/TEXT/AccessRequestHeader';

TEXT.DEFINE({
  /** Access Request Header Text */
  header: ({
    requesterNameAndEmail,
    resourceName,
  }: {
    requesterNameAndEmail: string;
    resourceName: string;
  }) => `${requesterNameAndEmail} requested access to ${resourceName}`,
});

/**
 * @description The `formatAccessRequestHeader` function transforms access request node data into
 * a notification header format. This function extracts the requester user information, determines
 * if the requester is an existing or new member, and formats the header text including the
 * requester's name (with email for new members), action text, and the requested resource name.
 *
 * @param {AccessRequestNodeFragment | DeepPartial<AccessRequestNodeFragment>} accessRequestNodeData the access request node data containing requester and resource information.
 *
 * @returns {Object} an object containing the formatted header information.
 * @property {string} text the action text describing the access request.
 */

export function formatAccessRequestHeader(
  accessRequestNodeData:
    | AccessRequestNodeFragment
    | DeepPartial<AccessRequestNodeFragment>
) {
  const { requesterUser, requestedResource } = accessRequestNodeData;

  const requesterNameAndEmail = `${
    requesterUser?.name ?? ''
  } (${requesterUser?.email})`;

  const resourceName = requestedResource?.name ?? '';

  const text = TEXT.header({
    requesterNameAndEmail,
    resourceName,
  });

  return { text };
}
